import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Drawer,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";

import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { getAllMuestras } from "../../api/Muestras/muestraApi";

import { getMarcas } from "../../api/Marcas/marcaApi";
import {
  getMaletaById,
  addMuestraToMaleta,
  getMuestrasInMaleta,
  removeMuestraFromMaleta,
  addMuestraToUbicacion,
} from "../../api/Maletas/maletasApi";

import Autocomplete from "@mui/material/Autocomplete";

const DrawerEditarMaleta = ({
  open,
  onClose,
  maletaId,
  onMuestrasUpdated,
  onMuestraAdded,
  onMuestraRemoved, // ✅ Agregado
}) => {
  const [marcas, setMarcas] = useState([]);
  const [marcaSeleccionada, setMarcaSeleccionada] = useState("");
  const [muestras, setMuestras] = useState([]); // Lista filtrada de muestras
  const [allMuestras, setAllMuestras] = useState([]); // Lista completa de muestras

  const [selectedMuestra, setSelectedMuestra] = useState("");
  const [maleta, setMaleta] = useState(null);
  const [muestrasAsignadas, setMuestrasAsignadas] = useState([]);
  const [loading, setLoading] = useState(false);

  // Estado para el diálogo de confirmación
  const [openDialog, setOpenDialog] = useState(false);
  const [muestraToDelete, setMuestraToDelete] = useState(null);
  const [buscando, setBuscando] = useState(false);

  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);

  useEffect(() => {
    if (open && maletaId) {
      fetchMaleta();
      fetchMuestrasAsignadas();
      fetchMarcas();
      fetchInitialMuestras(); // Cargar muestras iniciales
    }
  }, [open, maletaId]);

  const fetchMaleta = async () => {
    try {
      setLoading(true);
      const data = await getMaletaById(maletaId);
      setMaleta(data);
    } catch (error) {
      console.error("Error al obtener maleta:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeMarca = (event) => {
    setMarcaSeleccionada(event.target.value);
  };

  const fetchMarcas = async () => {
    try {
      const data = await getMarcas();
      setMarcas(data);
    } catch (error) {
      console.error("Error al cargar marcas:", error);
    }
  };

  // Cargar marcas desde la API al abrir el Drawer
  useEffect(() => {
    const fetchMarcas = async () => {
      try {
        const data = await getMarcas();
        setMarcas(data);
      } catch (error) {
        console.error("Error al cargar marcas:", error);
      }
    };

    fetchMarcas();
  }, []);

  /*   const fetchMuestras = async () => {
    try {
      const data = await getAllMuestras();
      setMuestras(data);
    } catch (error) {
      console.error("Error al obtener muestras:", error);
    }
  }; */

  const fetchMuestrasAsignadas = async () => {
    try {
      const data = await getMuestrasInMaleta(maletaId);
      setMuestrasAsignadas(data);
    } catch (error) {
      console.error("Error al obtener muestras asignadas:", error);
    }
  };

  const fetchInitialMuestras = async () => {
    try {
      const data = await getAllMuestras();
      setAllMuestras(data);
      setMuestras(data.slice(0, 10)); // Mostrar las primeras 10 muestras
    } catch (error) {
      console.error("Error al obtener muestras:", error);
    }
  };

  const buscarMuestras = (query) => {
    if (!query) {
      setMuestras(allMuestras.slice(0, 10)); // Volver a mostrar las primeras 10 si se borra la búsqueda
    } else {
      const filtered = allMuestras.filter((m) =>
        m.MUESTRA.toLowerCase().includes(query.toLowerCase())
      );
      setMuestras(filtered);
    }
  };

  const handleAddMuestra = async () => {
    if (!selectedMuestra) return;

    // Verificar si la muestra ya está en la maleta
    const isAlreadyAssigned = muestrasAsignadas.some(
      (m) => m.ID_MUESTRA === selectedMuestra.ID_MUESTRA
    );

    if (isAlreadyAssigned) {
      setOpenDuplicateDialog(true);
      return;
    }

    try {
      setLoading(true);
      await addMuestraToMaleta(maletaId, selectedMuestra.ID_MUESTRA);

      if (!marcaSeleccionada) {
        alert("Por favor selecciona una marca antes de agregar la muestra.");
        setLoading(false);
        return;
      }

      await addMuestraToUbicacion({
        muestraId: selectedMuestra.ID_MUESTRA,
        maletaId,
        ubicacion: `Cliente - ${maleta?.nombre}`,
        fecha: new Date().toISOString(),
        marcaId: parseInt(marcaSeleccionada, 10),
      });

      await fetchMuestrasAsignadas();
      if (onMuestraAdded) onMuestraAdded(maletaId);
      setSelectedMuestra(null);
    } catch (error) {
      console.error("Error al agregar muestra:", error);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Función para abrir el diálogo antes de eliminar
  const handleOpenDialog = (muestra) => {
    setMuestraToDelete(muestra);
    setOpenDialog(true);
  };

  // 🔹 Función para confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!muestraToDelete || !muestraToDelete.ID_MUESTRA) {
      console.error(
        "❌ No se puede eliminar: muestra inválida",
        muestraToDelete
      );
      return;
    }

    try {
      setLoading(true);

      const response = await removeMuestraFromMaleta(
        maletaId,
        muestraToDelete.ID_MUESTRA
      );

      if (!response.success) {
        console.error("❌ Error: La API no confirmó la eliminación correcta.");
        return;
      }

      // ✅ Filtrar la muestra eliminada del estado
      const nuevasMuestras = muestrasAsignadas.filter(
        (m) => m.ID_MUESTRA !== muestraToDelete.ID_MUESTRA
      );
      setMuestrasAsignadas(nuevasMuestras);

      // ✅ Si no quedan muestras en la maleta, actualizar Maletas.js
      if (nuevasMuestras.length === 0 && onMuestraRemoved) {
        onMuestraRemoved(maletaId, true);
      } else if (onMuestraRemoved) {
        onMuestraRemoved(maletaId);
      }

      // ✅ Cerrar diálogo correctamente
      setOpenDialog(false);
      setMuestraToDelete(null);
    } catch (error) {
      console.error("❌ Error al eliminar muestra:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 350, p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Editar Maleta: {maleta?.nombre || ""}
        </Typography>

        {/* Buscador de Muestras */}
        <Autocomplete
          fullWidth
          options={muestras}
          getOptionLabel={(option) => option.MUESTRA}
          loading={buscando}
          onInputChange={(_, newInputValue) => buscarMuestras(newInputValue)}
          onChange={(_, newValue) => setSelectedMuestra(newValue)}
          openOnFocus
          renderInput={(params) => (
            <TextField
              {...params}
              label="Buscar Muestra"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {buscando ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {/* Selección de Marca */}
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Marca</InputLabel>
          <Select value={marcaSeleccionada} onChange={handleChangeMarca}>
            <option value="">Selecciona una marca</option>
            {marcas.map((marca) => (
              <MenuItem key={marca.ID_MARCA} value={marca.ID_MARCA}>
                {marca.MARCA}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={handleAddMuestra}
          disabled={!selectedMuestra || loading}
          fullWidth
          sx={{ mt: 2, backgroundColor: "rgb(144 178 183)", color: "#000" }}
        >
          {loading ? <CircularProgress size={24} /> : "Añadir Muestra"}
        </Button>

        {/* Lista de muestras asignadas */}
        <Typography variant="h6" sx={{ mt: 2 }}>
          Muestras en esta maleta:
        </Typography>
        <List>
          {muestrasAsignadas.map((muestra) => (
            <ListItem key={muestra.ID_MUESTRA}>
              <ListItemText primary={muestra.MUESTRA} />
              <IconButton
                onClick={() => handleOpenDialog(muestra)}
                color="error"
                sx={{
                  "&.MuiButtonBase-root": {
                    height: "1.8em",
                    marginLeft: "5px",
                    marginTop: "6px",
                    border: "1px solid #90b2b7",
                  },

                  "&:hover": {
                    backgroundColor: "rgb(144 178 183)",
                    color: "red",
                  },
                }}
              >
                <DeleteOutlinedIcon />
              </IconButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* 🔹 Diálogo de confirmación de eliminación */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setMuestraToDelete(null);
        }}
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que quieres eliminar la muestra{" "}
            <strong>{muestraToDelete?.MUESTRA}</strong> de esta maleta?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setMuestraToDelete(null);
            }}
            color="primary"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            disabled={loading}
          >
            {loading ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDuplicateDialog}
        onClose={() => setOpenDuplicateDialog(false)}
      >
        <DialogTitle>Muestra duplicada</DialogTitle>
        <DialogContent>
          <Typography>Esta muestra ya está en la maleta.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDuplicateDialog(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default DrawerEditarMaleta;
